import { getErrorMessage } from "../utils/errorMessage";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { accessAPI, roleAPI } from "../api/client";
import LoadingButton from "../components/LoadingButton";
import ConfirmModal from "../components/ConfirmModal";
import { toast } from "react-toastify";

// =============================================================================
// ACCESS DEPENDENCIES — interconnected pages/actions.
// Granting a dependent page grants its requirements too, and revoking a
// requirement is blocked while the dependent page stays granted. Keep this
// single map in sync when flows change.
//
// Two rules are modelled here:
//   1. EVERY action depends on its own page's VIEW. A No Role / custom-role
//      employee has no legacy permission set, and the sidebar + route guards
//      are VIEW-gated — so granting Add/Edit without VIEW gives them a button
//      they can never reach. Auto-granting VIEW makes a lone Edit/Add grant
//      actually usable (the "give add if they selected only edit" case).
//   2. Pages whose tabs load data from ANOTHER page in the same session
//      depend on that page's access (e.g. the Job dashboard fetches
//      candidates + interviews, Candidate Add loads the job dropdown).
//   "page.action that is granted" -> ["page.action it requires", ...]
// =============================================================================
const ACCESS_DEPENDENCIES = {
  // --- Own-page VIEW first (an action is useless without its page) ---
  "candidate.add": ["candidate.view", "ai.use", "job.view"],
  "candidate.edit": ["candidate.view", "job.view"],
  "candidate.delete": ["candidate.view"],
  "job.add": ["job.view"],
  "job.edit": ["job.view"],
  "job.export": ["job.view"],
  "employee.add": ["employee.view"],
  "employee.edit": ["employee.view"],
  "employee.delete": ["employee.view"],
  "trainee.add": ["trainee.view"],
  "trainee.edit": ["trainee.view"],
  "trainee.delete": ["trainee.view"],
  "trainee.export": ["trainee.view"],
  "intern.add": ["intern.view"],
  "intern.edit": ["intern.view"],
  "intern.delete": ["intern.view"],
  "intern.export": ["intern.view"],
  "offer.create": ["offer.view"],
  "offer.manage": ["offer.view"],
  // Revenue Tracker + Variable Tracker add/edit forms search the employee
  // roster for the allocation/employee picker (see payrolltracker.js:120,
  // Variabletracker.js:153).
  "payroll.edit": ["payroll.view", "employee.view"],
  "payroll.export": ["payroll.view"],
  "variable.edit": ["variable.view", "employee.view"],
  "variable.export": ["variable.view"],
  "resource_cost.edit": ["resource_cost.view"],
  "resource_cost.export": ["resource_cost.view"],
  "customer.add": ["customer.view"],
  "customer.edit": ["customer.view"],
  "customer.delete": ["customer.view"],
  "project.add": ["project.view", "customer.view"],
  "project.edit": ["project.view", "customer.view"],
  "project.delete": ["project.view"],
  "sales.create": ["sales.view"],
  "sales.edit": ["sales.view"],
  "sales.delete": ["sales.view"],
  "sales.import": ["sales.view"],
  "interview.manage": ["interview.view", "candidate.view", "job.view"],
  "headcount.export": ["headcount.view"],
  "headcount.import": ["headcount.view"],
  "headcount.sync": ["headcount.view"],
  "master.manage": ["master.view", "customer.view", "project.view"],

  // --- Interconnected pages that load data from other tabs ---
  // Job dashboard & Jobs list load candidate + interview data.
  "job.view": ["candidate.view", "interview.view"],
  // Interviews page loads candidates + job positions + interviewers.
  "interview.view": ["candidate.view", "job.view"],
  // Create/Edit Offer modals load candidates + headcount options.
  "offer.view": ["candidate.view", "headcount.view"],
  // Resource Cost Master tab loads the project resource allocation map and
  // searches the employee roster for the allocation picker.
  "resource_cost.view": ["project.view", "employee.view"],
  // Revenue Tracker add/edit forms search the employee roster.
  "payroll.view": ["employee.view"],
  // TA Dashboard KPIs are built from candidates + interviews + offers.
  "dashboard.view": ["candidate.view", "interview.view", "offer.view"],
};

// Reverse index: a page.action -> the features that require it. Used to block
// revoking a requirement while one of its dependents is still granted.
const DEPENDENT_BY = {};
for (const [parent, deps] of Object.entries(ACCESS_DEPENDENCIES)) {
  for (const dep of deps) {
    if (!DEPENDENT_BY[dep]) DEPENDENT_BY[dep] = [];
    if (!DEPENDENT_BY[dep].includes(parent)) DEPENDENT_BY[dep].push(parent);
  }
}

function splitPageAction(key) {
  const i = key.indexOf(".");
  return [key.slice(0, i), key.slice(i + 1)];
}

// Team divisions in displayOrder sequence (metadata-only; never affects access).
function pageDivisions(pages) {
  const seen = [];
  for (const p of pages) {
    if (!seen.includes(p.division)) seen.push(p.division);
  }
  return seen;
}

// Geometry for the portalled row-action menu: anchored to the trigger
// button's viewport rect, flipped upward when there is more room above than
// below. Pure — also used to re-anchor the open menu while scrolling.
function roleMenuPosition(rect) {
  const viewportW = document.documentElement.clientWidth;
  const MENU_H = 210; // rendered height of the 5-item menu
  const MENU_W = 200; // rendered width (min-width 176 + padding + border)
  const GAP = 6; // gap left between the trigger and the menu
  const EDGE = 10; // clearance kept from the top/bottom viewport edge
  // Keep the right edge aligned with the button but never off-screen.
  const right = Math.round(
    Math.max(8, Math.min(viewportW - rect.right, viewportW - MENU_W))
  );
  const spaceBelow = window.innerHeight - rect.bottom - GAP - EDGE;
  const spaceAbove = rect.top - GAP - EDGE;
  const flipUp = spaceBelow < MENU_H && spaceAbove > spaceBelow;
  // Constrain the menu to the room that really exists on the chosen side;
  // .rm-menu is overflow-y: auto, so items past that limit stay reachable.
  const maxH = Math.max(140, Math.round(flipUp ? spaceAbove : spaceBelow));
  return flipUp
    ? { bottom: Math.round(window.innerHeight - rect.top + GAP), right, maxH }
    : { top: Math.round(rect.bottom + GAP), right, maxH };
}

// True while the menu's trigger button is still painted: inside the viewport
// AND not fully clipped away by a scrolling/hidden ancestor (the roles table
// scrolls inside its own wrapper, independently of the page scroll).
function isMenuTriggerVisible(el) {
  if (!el || !document.body.contains(el)) return false;
  const r = el.getBoundingClientRect();
  if (
    r.bottom <= 0 ||
    r.top >= window.innerHeight ||
    r.right <= 0 ||
    r.left >= window.innerWidth
  ) {
    return false;
  }
  let p = el.parentElement;
  while (p && p !== document.body) {
    const cs = window.getComputedStyle(p);
    if (cs.overflowX !== "visible" || cs.overflowY !== "visible") {
      const pr = p.getBoundingClientRect();
      if (
        r.bottom <= pr.top + 1 ||
        r.top >= pr.bottom - 1 ||
        r.right <= pr.left + 1 ||
        r.left >= pr.right - 1
      ) {
        return false;
      }
    }
    p = p.parentElement;
  }
  return true;
}

// Access Management - merged page combining UserAccessControl (per-employee
// page/action access overrides) and RoleManagement (role CRUD + role
// permission matrix). Previously two files; now one file per layer.
//
// - `view` ("uac" | "roles") selects the sub-view rendered under Admin's
//   main tabs (there is no internal tab switcher): Admin.js renders
//   <AccessManagement view="uac" /> for its "uac" tab and view="roles" for
//   its "roles" tab.
// - pages + users are fetched once and shared by both sub-tabs.
// - Endpoints, request/response handling, No-Role semantics and styling are
//   unchanged from the originals. Role-management state names are prefixed
//   where they collided with UAC state (access -> roleAccess, usersLoading ->
//   roleUsersLoading, savingAccess -> roleAccessSaving, handleSaveAccess ->
//   handleSaveRoleAccess).
export default function AccessManagement({ view = "uac" }) {
  /* =============== SHARED DATA =============== */
  // Page/action matrix + users list, shared by both sub-tabs.
  const [pages, setPages] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  /* =============== USER ACCESS CONTROL (UAC) =============== */
  const [manageUser, setManageUser] = useState(null);
  const [access, setAccess] = useState({});
  const [roleAccessMap, setRoleAccessMap] = useState({});
  const [effectiveMap, setEffectiveMap] = useState({});
  const [accessLoading, setAccessLoading] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);
  /* ============ ROLES TAB: OPEN ROW MENU (UI ONLY) ============ */
  /* Tracks which row's "..." menu is open. Purely presentational: it does not
     touch role data, permissions, or any handler. */
  const [openRoleMenu, setOpenRoleMenu] = useState(null);
  /* Viewport coordinates for the open menu. The menu is portalled to
     document.body and position: fixed, so it escapes the table wrapper's
     `overflow: hidden` clip and never gets painted over by neighbour rows
     (that stacking/clip is what used to slice the menu with row backgrounds). */
  const [roleMenuPos, setRoleMenuPos] = useState(null);
  const [roleToToggle, setRoleToToggle] = useState(null);
  const [togglingRole, setTogglingRole] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deletingRole, setDeletingRole] = useState(false);
  /* The trigger button of the open menu, kept so scroll/resize can re-measure
     it without another click. */
  const roleMenuTriggerRef = useRef(null);
  const closeRoleMenu = () => {
    setOpenRoleMenu(null);
    setRoleMenuPos(null);
    roleMenuTriggerRef.current = null;
  };
  /* Skip the state update (and re-render) when a scroll tick would not
     actually move the menu. */
  const applyRoleMenuPos = (next) => {
    setRoleMenuPos((prev) =>
      prev &&
      prev.top === next.top &&
      prev.bottom === next.bottom &&
      prev.right === next.right &&
      prev.maxH === next.maxH
        ? prev
        : next
    );
  };
  /* Anchor the menu to its trigger button; flip upward near the viewport edge. */
  const toggleRoleMenu = (e, id) => {
    if (openRoleMenu === id) {
      closeRoleMenu();
      return;
    }
    const el = e.currentTarget;
    roleMenuTriggerRef.current = el;
    applyRoleMenuPos(roleMenuPosition(el.getBoundingClientRect()));
    setOpenRoleMenu(id);
  };
  // Outside click and Escape dismiss the row menu (presentation only).
  // Scroll and resize do NOT close it: the roles table scrolls inside its own
  // wrapper and the page scrolls too, so any residual/inertial scroll right
  // after opening used to fire the old close-on-scroll handler and make the
  // menu vanish the moment you tried to click it at the bottom of the list.
  // Instead the menu is re-anchored to follow its trigger, and is only closed
  // once the trigger button scrolls (or is clipped) out of view.
  useEffect(() => {
    if (openRoleMenu === null) return undefined;
    const reanchor = () => {
      const el = roleMenuTriggerRef.current;
      if (!isMenuTriggerVisible(el)) {
        closeRoleMenu();
        return;
      }
      applyRoleMenuPos(roleMenuPosition(el.getBoundingClientRect()));
    };
    const onDown = (e) => {
      // The menu is portalled, so clicks inside it no longer sit inside the
      // trigger wrapper — allow both, otherwise the items can never be clicked.
      if (
        !e.target.closest(".rm-row-actions") &&
        !e.target.closest(".rm-menu")
      ) {
        closeRoleMenu();
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") closeRoleMenu();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", reanchor);
    document.addEventListener("scroll", reanchor, {
      capture: true,
      passive: true,
    });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", reanchor);
      document.removeEventListener("scroll", reanchor, { capture: true });
    };
  }, [openRoleMenu]);
  /* ============ UAC PAGINATION (UI only — 25 users/page) ============ */
  const UAC_USERS_PER_PAGE = 25;
  const [uacPage, setUacPage] = useState(1);

  const fetchUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const res = await accessAPI.getUsers();
      setUsers(res.data || []);
    } catch (err) {
      console.error("Fetch users failed", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Sudo admin authentication required");
      }
    } finally {
      setUsersLoading(false);
    }
  }, []);


  /* ================= MANAGE ACCESS ================= */
  const openManageAccess = async (user) => {
    setManageUser(user);
    // Reset all matrices up front so a failed/slow fetch can never leave the
    // previous user's overrides or effective state on screen.
    setAccess({});
    setRoleAccessMap({});
    setEffectiveMap({});
    setAccessLoading(true);
    try {
      const res = await accessAPI.getUserAccess(user.id);
      const data = res.data || {};
      setAccess(data.access || {});
      setRoleAccessMap(data.roleAccess || {});
      setEffectiveMap(data.effective || {});
    } catch (err) {
      toast.error("Failed to load access");
    } finally {
      setAccessLoading(false);
    }
  };

  // Access state shape: { "page.action": "allow" | "deny" } — absence means
  // DEFAULT (inherit role permission, else DENY).
  // Effective priority (preserved for role users):
  //   employee DENY > employee ALLOW > role permission > default DENY.
  // For No Role users there is no role baseline, so:
  //   checked (allow row) = explicitly granted,
  //   unchecked (no row) = denied. Explicit deny rows are meaningless for
  //   No Role users and are dropped on save (absence == DENY).
  const isNoRoleUser = (u) => !u || !String(u.role || "").trim();
  const isNoRole = isNoRoleUser(manageUser);

  const getState = (page, action) => {
    const key = `${page}.${action}`;
    if (!(key in access)) return "default";
    return access[key];
  };

  // Checkbox mode (No Role only): true when an explicit ALLOW row exists.
  // A stored 'deny' row (legacy) also renders unchecked — it grants nothing.
  const isChecked = (page, action) => access[`${page}.${action}`] === "allow";

  // RESOLVED/EFFECTIVE result for a single "page.action", mirroring the
  // backend enforcement priority (employee DENY > employee ALLOW > role
  // permission > default DENY). The employee override branch is read live from
  // `access` state so the badge flips the moment the admin clicks a control;
  // the DEFAULT/inherit branch uses the backend-computed `effective` map
  // (which already accounts for the role matrix, legacy fallback and
  // super_admin), so the modal always matches what the user actually gets.
  const effectiveFor = (page, action) => {
    const key = `${page}.${action}`;
    const override = access[key];
    if (override === "allow") return true;
    if (override === "deny") return false;
    return Boolean(effectiveMap[key]);
  };

  // Interconnected-page handling: a "page.action" that other tabs depend on
  // (see ACCESS_DEPENDENCIES above) is auto-granted whenever one of its
  // dependents is granted. This keeps interconnected tabs (e.g. No Role users
  // with only the Job page) from failing mid-page because a sibling API
  // returned 403.
  const accessLabel = (key) => {
    const [page, action] = splitPageAction(key);
    const p = pages.find((x) => x.page === page);
    if (!p) return key;
    const a = p.actions.find((x) => x.action === action);
    return `${p.label} › ${a ? a.label : action.toUpperCase()}`;
  };

// Warn (but do NOT block) when revoking a CROSS-PAGE requirement that a
// dependent tab on another page still needs. The admin stays in control —
// they may break an interconnected page, but a toast explains the risk.
// Same-page requirements are never blocked either; they cascade-revoke
// together (select all / remove all), and the TA trio (Candidates / Jobs /
// Interviews VIEW) comes and goes as one unit.
const warnCrossPageRevoke = (page, action, grantedCheck) => {
    const key = `${page}.${action}`;
    const parents = (DEPENDENT_BY[key] || []).filter((parent) => {
      const [pp] = splitPageAction(parent);
      return pp !== page && grantedCheck(parent);
    });
    if (!parents.length) return;
    const parentLabels = parents.map(accessLabel).join(" and ");
    toast.info(
      `${accessLabel(key)} access is required for ${parentLabels}. Without it, ${parentLabels} will not work.`
    );
  };

  // Revoking one action on a page also revokes the page's other granted
  // actions ("select all 3 or remove all three"). Only same-page dependents
  // are removed; cross-page dependents are merely warned, never blocked.
  const cascadeSamePageRevoke = (state, page, key) => {
    const next = { ...state };
    for (const dep of DEPENDENT_BY[key] || []) {
      const [depPage] = splitPageAction(dep);
      if (depPage === page && next[dep] === "allow") delete next[dep];
    }
    return next;
  };

  // Transitive closure of a "page.action"'s requirements. Dependencies can
  // depend on further pages (e.g. candidate.add -> job.view -> candidate.view
  // + interview.view), and the map contains cycles (job.view <-> interview.view),
  // so a seen-set is required — collectDeps never recurses into a key twice
  // and the whole closure is granted together.
  const collectDeps = (key, acc = [], seen = new Set()) => {
    const deps = ACCESS_DEPENDENCIES[key];
    if (!deps || !deps.length) return acc;
    for (const d of deps) {
      if (seen.has(d)) continue;
      seen.add(d);
      if (!acc.includes(d)) acc.push(d);
      collectDeps(d, acc, seen);
    }
    return acc;
  };

  const autoGrant = (key) => {
    const deps = collectDeps(key);
    if (!deps.length) return {};
    return Object.fromEntries(deps.map((d) => [d, "allow"]));
  };

  // Cross-page requirements for a page ("this page also needs OTHER pages").
  // Used only for the UI banner — the actual grant still comes from
  // collective deps in the checkbox/toggle handlers above.
  const crossPageReqsFor = (page) => {
    const reqs = new Set();
    for (const [key, deps] of Object.entries(ACCESS_DEPENDENCIES)) {
      const [kPage] = splitPageAction(key);
      if (kPage !== page) continue;
      for (const dep of deps) {
        const [dPage] = splitPageAction(dep);
        if (dPage !== page) reqs.add(dep);
      }
    }
    return [...reqs];
  };

  // The TA trio (Candidates / Jobs / Interviews VIEW) are tightly coupled —
  // a user who needs one almost always needs the others.  Toggling any one
  // of these three automatically toggles the other two so the admin can
  // grant or revoke them as a single group instead of playing whack-a-mole.
  const TA_VIEW_TRIO = new Set(["candidate.view", "job.view", "interview.view"]);

  const setState = (page, action, value) => {
    const key = `${page}.${action}`;
    setAccess((prev) => {
      let next = { ...prev };
      if (value !== "allow") {
        warnCrossPageRevoke(page, action, (parent) =>
          effectiveFor(...splitPageAction(parent))
        );
      }
      if (value === "default") delete next[key];
      else next[key] = value;
      if (value === "allow") Object.assign(next, autoGrant(key));
      else {
        next = cascadeSamePageRevoke(next, page, key);
        if (TA_VIEW_TRIO.has(key)) {
          for (const other of TA_VIEW_TRIO) {
            if (other !== key && next[other] === "allow") {
              next = cascadeSamePageRevoke(next, ...splitPageAction(other));
              delete next[other];
            }
          }
        }
      }
      return next;
    });
  };

  const toggleChecked = (page, action) => {
    const key = `${page}.${action}`;
    setAccess((prev) => {
      let next = { ...prev };
      if (next[key] === "allow") {
        // ── Uncheck: remove this key + same-page cascade ──
        delete next[key];
        next = cascadeSamePageRevoke(next, page, key);

        // Group-deselect: if the key is part of the TA trio, also remove
        // the other two members (and their same-page cascades).
        if (TA_VIEW_TRIO.has(key)) {
          for (const other of TA_VIEW_TRIO) {
            if (other !== key && next[other] === "allow") {
              delete next[other];
              const [op] = splitPageAction(other);
              next = cascadeSamePageRevoke(next, op, other);
            }
          }
        }
      } else {
        // ── Check: grant this key + transitive auto-grant ──
        next[key] = "allow";
        Object.assign(next, autoGrant(key));
      }
      return next;
    });
  };

  const handleSaveAccess = async () => {
    setSavingAccess(true);
    try {
      // No Role: persist ONLY explicit grants. Unchecked actions are omitted
      // (absence = DENY), and any legacy 'deny' rows are dropped — they are
      // semantically identical to absence for a user with no role baseline.
      // Role users: persist the full tri-state map untouched so an explicit
      // employee DENY stays distinguishable from inherited role permission.
      let payload = access;
      if (isNoRole) {
        payload = {};
        for (const [key, value] of Object.entries(access)) {
          if (String(value).toLowerCase() === "allow") {
            payload[key] = "allow";
            // Persist a dependent page's requirements too (e.g. Candidate ADD
            // -> AI USE, Job VIEW -> Candidate + Interview VIEW), so applied
            // access always includes what an interconnected tab needs.
            Object.assign(payload, autoGrant(key));
          }
        }
      } else {
        // Role users: keep the tri-state map but still materialise the
        // requirements of every explicitly granted action.
        payload = { ...access };
        for (const [key, value] of Object.entries(payload)) {
          if (String(value).toLowerCase() === "allow") {
            Object.assign(payload, autoGrant(key));
          }
        }
      }
      // accessAPI.saveUserAccess(userId, accessMap) already wraps the body as
      // { access: accessMap } (see api/client.js). Pass the flat map here —
      // wrapping it again as { access: payload } would send
      // { access: { access: {...} } }, backend would see key "access" with
      // value "[object Object]" (not allow/deny), skip every tuple and persist
      // ZERO rows (DELETE all, INSERT none).
      const res = await accessAPI.saveUserAccess(manageUser.id, payload);
      setAccess(payload);
      toast.success(
        `Access saved for ${manageUser.name} (${res.data?.saved ?? 0} permission(s))`
      );
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save access"));
    } finally {
      setSavingAccess(false);
    }
  };

  /* ============ PAGINATION SLICE (UI only) ============ */
  const uacTotalPages = Math.max(1, Math.ceil(users.length / UAC_USERS_PER_PAGE));
  const uacSafePage = Math.min(uacPage, uacTotalPages);
  const pagedUacUsers = users.slice(
    (uacSafePage - 1) * UAC_USERS_PER_PAGE,
    uacSafePage * UAC_USERS_PER_PAGE
  );

  /* =============== ROLE MANAGEMENT (RM) =============== */
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [loadingError, setLoadingError] = useState("");

  /* =============== CREATE / EDIT FORM =============== */
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [editingRole, setEditingRole] = useState(null);
  const [savingRole, setSavingRole] = useState(false);
  const formRef = useRef(null);

  /* =============== PERMISSIONS MODAL =============== */
  const [permRole, setPermRole] = useState(null);
  const [roleAccess, setRoleAccess] = useState({});
  const [roleAccessLoading, setRoleAccessLoading] = useState(false);
  const [roleAccessSaving, setRoleAccessSaving] = useState(false);

  /* =============== USERS MODAL =============== */
  const [usersRole, setUsersRole] = useState(null);
  const [roleUsers, setRoleUsers] = useState([]);
  const [roleUsersLoading, setRoleUsersLoading] = useState(false);
  const [assignUserId, setAssignUserId] = useState("");
  const [assigning, setAssigning] = useState(false);

  const fetchRoles = useCallback(async () => {
    try {
      setRolesLoading(true);
      const res = await roleAPI.getRoles();
      setRoles(res.data || []);
    } catch (err) {
      console.error("Fetch roles failed", err);
      setLoadingError(getErrorMessage(err, "Failed to load roles"));
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Sudo admin authentication required");
      }
    } finally {
      setRolesLoading(false);
    }
  }, []);


  /* =============== RESET FORM =============== */
  const resetForm = () => {
    setFormName("");
    setFormDescription("");
    setEditingRole(null);
  };

  /* =============== CREATE / EDIT ROLE =============== */
  const handleSaveRole = async () => {
    if (!formName.trim()) {
      toast.error("Role name is required");
      return;
    }
    setSavingRole(true);
    try {
      if (editingRole) {
        await roleAPI.updateRole(editingRole.id, {
          name: formName,
          description: formDescription,
        });
        toast.success("Role updated");
      } else {
        await roleAPI.createRole({ name: formName, description: formDescription });
        toast.success("Role created");
      }
      resetForm();
      await fetchRoles();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save role"));
    } finally {
      setSavingRole(false);
    }
  };

  const handleStartEdit = (role) => {
    setEditingRole(role);
    setFormName(role.name);
    setFormDescription(role.description || "");
    // The form sits above the roles table — when Edit is picked from a row at
    // the bottom of the list it would otherwise open off-screen and look like
    // the menu item did nothing.
    requestAnimationFrame(() =>
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    );
  };

  const handleToggleActive = (role) => {
    setRoleToToggle(role);
  };

  const confirmToggleActive = async () => {
    if (!roleToToggle || togglingRole) return;
    const role = roleToToggle;
    const nextActive = !(Number(role.is_active) === 1);
    try {
      setTogglingRole(true);
      await roleAPI.toggleRole(role.id, nextActive ? 1 : 0);
      toast.success(nextActive ? "Role enabled" : "Role disabled");
      setRoleToToggle(null);
      await fetchRoles();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update role status"));
    } finally {
      setTogglingRole(false);
    }
  };

  const handleDeleteRole = (role) => {
    setRoleToDelete(role);
  };

  const confirmDeleteRole = async () => {
    if (!roleToDelete || deletingRole) return;
    const role = roleToDelete;
    try {
      setDeletingRole(true);
      await roleAPI.deleteRole(role.id);
      toast.success("Role deleted");
      if (editingRole?.id === role.id) resetForm();
      setRoleToDelete(null);
      await fetchRoles();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete role"));
    } finally {
      setDeletingRole(false);
    }
  };

  /* =============== PERMISSIONS MODAL =============== */
  const openPermissions = async (role) => {
    setPermRole(role);
    setRoleAccess({});
    setRoleAccessLoading(true);
    try {
      const res = await roleAPI.getRoleAccess(role.id);
      setPages(res.data?.pages || pages);
      setRoleAccess(res.data?.access || {});
    } catch (err) {
      toast.error("Failed to load role access");
    } finally {
      setRoleAccessLoading(false);
    }
  };

  const setPermission = (page, action, value) => {
    const key = `${page}.${action}`;
    if (value === "deny") {
      warnCrossPageRevoke(page, action, (parent) => {
        const [pp, pa] = splitPageAction(parent);
        return roleAccess[`${pp}.${pa}`] === "allow";
      });
    }
    setRoleAccess((prev) => {
      let next = { ...prev, [key]: value };
      if (value === "allow") Object.assign(next, autoGrant(key));
      else {
        next = cascadeSamePageRevoke(next, page, key);
        // Group-deselect: Candidates / Jobs / Interviews VIEW come and go
        // together (they load each other's data on the same dashboard).
        if (TA_VIEW_TRIO.has(key)) {
          for (const other of TA_VIEW_TRIO) {
            if (other !== key && next[other] === "allow") {
              next = cascadeSamePageRevoke(next, ...splitPageAction(other));
              delete next[other];
            }
          }
        }
      }
      return next;
    });
  };

  const getPermission = (page, action) => roleAccess[`${page}.${action}`] || "deny";

  const handleSaveRoleAccess = async () => {
    setRoleAccessSaving(true);
    try {
      await roleAPI.saveRoleAccess(permRole.id, roleAccess);
      toast.success("Role permissions saved");
      await fetchRoles();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save role access"));
    } finally {
      setRoleAccessSaving(false);
    }
  };

  /* =============== USERS MODAL =============== */
  const openUsers = async (role) => {
    setUsersRole(role);
    setRoleUsers([]);
    setAssignUserId("");
    setRoleUsersLoading(true);
    try {
      const res = await roleAPI.getRoleUsers(role.id);
      setRoleUsers(res.data?.users || []);
      if (!users.length) {
        const u = await accessAPI.getUsers();
        setUsers(u.data || []);
      }
    } catch (err) {
      toast.error("Failed to load role users");
    } finally {
      setRoleUsersLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!assignUserId) {
      toast.error("Select a user to assign");
      return;
    }
    setAssigning(true);
    try {
      await roleAPI.assignUsers(usersRole.id, [Number(assignUserId)]);
      toast.success("User assigned");
      const res = await roleAPI.getRoleUsers(usersRole.id);
      setRoleUsers(res.data?.users || []);
      setAssignUserId("");
      await fetchRoles();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to assign user"));
    } finally {
      setAssigning(false);
    }
  };

  const assignedIds = new Set(roleUsers.map((u) => u.id));
  const availableUsers = users.filter((u) => !assignedIds.has(u.id));


  /* =============== INIT (shared) =============== */
  useEffect(() => {
    fetchUsers();
    fetchRoles();
    accessAPI
      .getPages()
      .then((res) => setPages(res.data?.pages || []))
      .catch(() => toast.error("Failed to load pages & actions"));
  }, [fetchUsers, fetchRoles]);

  return (
    <div style={styles.page}>
      <style>{css}</style>

      {/* =============== USER ACCESS CONTROL VIEW =============== */}
      {view === "uac" && (
        <>
      {/* ============ TITLE ============ */}
      <div className="uac-header">
        <div>
          <h2>User Access Control</h2>
          <p className="uac-subtitle">
            Configure employee-specific access for each HR Portal page and action
          </p>
        </div>
      </div>

      {/* ============ USERS LIST ============ */}
      <div className="uac-card">
        <div className="uac-table-section">
          <h3>Employees ({users.length})</h3>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th scope="col">Employee</th>
                  <th scope="col">Role</th>
                  <th scope="col">Department</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersLoading && (
                  <tr>
                    <td colSpan="5">Loading employees...</td>
                  </tr>
                )}
                {pagedUacUsers.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <strong>{u.name}</strong>
                      <div className="uac-email">{u.email}</div>
                    </td>
                    <td>{u.role ? <span className="role-badge">{u.role}</span> : <span className="no-role">No Role</span>}</td>
                    <td>{u.department || "-"}</td>
                    <td>
                      <span
                        className={`uac-status ${
                          Number(u.is_active) === 1 ? "active" : "disabled"
                        }`}
                      >
                        {Number(u.is_active) === 1 ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td>
                      <div className="uac-row-actions">
                        <button
                          className="btn uac-manage"
                          onClick={() => openManageAccess(u)}
                        >
                          Manage Access
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!usersLoading && users.length === 0 && (
                  <tr>
                    <td colSpan="5">No employees found</td>
                  </tr>
                )}
              </tbody>
            </table>
            {users.length > UAC_USERS_PER_PAGE && (
              <div className="table-pagination">
                <button onClick={() => setUacPage(uacSafePage - 1)} disabled={uacSafePage <= 1}>
                  ‹ Prev
                </button>
                <span className="page-info">
                  Page {uacSafePage} of {uacTotalPages} ({users.length} employees)
                </span>
                <button
                  onClick={() => setUacPage(uacSafePage + 1)}
                  disabled={uacSafePage >= uacTotalPages}
                >
                  Next ›
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============ MANAGE ACCESS VIEW ============ */}
      {manageUser && (
        <div className="uac-manage-overlay">
          <div className="uac-manage-modal">
            <div className="uac-manage-header">
              <div className="uac-manage-title">
                <h3>Manage Access</h3>
                <p>
                  <strong>{manageUser.name}</strong>
                  <span className="uac-sep">|</span>
                  Role: <strong>{manageUser.role || "No Role"}</strong>
                  <span className="uac-sep">|</span>
                  Status:{" "}
                  <strong>
                    {Number(manageUser.is_active) === 1 ? "Active" : "Disabled"}
                  </strong>
                </p>
              </div>
              <button
                className="btn uac-close"
                onClick={() => setManageUser(null)}
              >
                ×
              </button>
            </div>

            {accessLoading ? (
              <p className="uac-empty">Loading access...</p>
            ) : (
              <div className="uac-access-body">
                {isNoRole && (
                  <p className="uac-hint">
                    No Role user — tick a box to grant that permission. Unticked
                    permissions stay denied; nothing is inherited from any role.
                  </p>
                )}
                {pageDivisions(pages).map((division) => (
                  <div className="uac-access-team" key={division}>
                    <h3 className="uac-access-team-title">{division}</h3>
                    {pages.filter((p) => p.division === division).map((p) => (
                      <div className="uac-access-page" key={p.page}>
                        <div className="uac-access-page-head">
                          <span className="uac-access-label">{p.label}</span>
                          {crossPageReqsFor(p.page).length > 0 && (
                            <span className="uac-page-deps">
                              Also needs:{" "}
                              {crossPageReqsFor(p.page).map(accessLabel).join(" + ")}
                            </span>
                          )}
                        </div>
                        <div className="uac-access-actions">
                          {p.actions.map((a) => {
                            // No Role: simple checkbox (granted / not granted).
                            if (isNoRole) {
                              const checked = isChecked(p.page, a.action);
                              return (
                                <div className="uac-access-action" key={a.action}>
                                  <label
                                    className={`uac-check ${checked ? "checked" : ""}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => toggleChecked(p.page, a.action)}
                                    />
                                    <span className="uac-action-name">{a.label}</span>
                                  </label>
                                  <span
                                    className={`uac-effective ${checked ? "ok" : "no"}`}
                                  >
                                    {checked ? "Effective: ALLOWED" : "Effective: DENIED"}
                                    {!checked && <em> · no grant</em>}
                                  </span>
                                </div>
                              );
                            }
                            // Role users: tri-state ALLOW / DEFAULT / DENY.
                            const state = getState(p.page, a.action);
                            const eff = effectiveFor(p.page, a.action);
                            const inherited = state === "default";
                            const roleBase = String(
                              roleAccessMap[`${p.page}.${a.action}`] || ""
                            ).toLowerCase();
                            return (
                              <div className="uac-access-action" key={a.action}>
                                <span className="uac-action-name">{a.label}</span>
                                <div className="uac-toggle-group">
                                  <button
                                    type="button"
                                    className={`uac-toggle ${
                                      state === "allow" ? "allow active" : ""
                                    }`}
                                    onClick={() => setState(p.page, a.action, "allow")}
                                  >
                                    ALLOW
                                  </button>
                                  <button
                                    type="button"
                                    className={`uac-toggle ${
                                      state === "default" ? "default active" : ""
                                    }`}
                                    onClick={() => setState(p.page, a.action, "default")}
                                  >
                                    DEFAULT
                                  </button>
                                  <button
                                    type="button"
                                    className={`uac-toggle ${
                                      state === "deny" ? "deny active" : ""
                                    }`}
                                onClick={() => setState(p.page, a.action, "deny")}
                              >
                                DENY
                              </button>
                            </div>
                            <span
                              className={`uac-effective ${eff ? "ok" : "no"}`}
                            >
                              {eff ? "Effective: ALLOWED" : "Effective: DENIED"}
                              {inherited && (
                                <em>
                                  {roleBase === "allow"
                                    ? " · inherited from role (ALLOW)"
                                    : " · inherited from role (deny/missing)"}
                                </em>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                </div>
              ))}
              </div>
            )}

            <div className="uac-manage-footer">
              <p className="uac-hint">
                {isNoRole
                  ? "Checked = explicitly granted to this user. Unchecked = denied (nothing inherited)."
                  : "ALLOW / DENY override the employee's role baseline for that action. DEFAULT inherits the role's permission (falls back to DENY when the role has none). The Effective badge shows the real outcome. Effective priority: employee DENY > employee ALLOW > role permission > default DENY."}
              </p>
              <div className="uac-manage-actions">
                <button
                  className="btn uac-outline"
                  onClick={() => setManageUser(null)}
                >
                  Cancel
                </button>
                <LoadingButton
                  onClick={handleSaveAccess}
                  loading={savingAccess}
                  loadingText="Saving..."
                >
                  Save Access
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* =============== ROLE MANAGEMENT VIEW =============== */}
      {view === "roles" && (
        <>

      {/* ============ TITLE ============ */}
      <div className="rm-header">
        <div>
          <h2>Role Management</h2>
          <p className="rm-subtitle">
            Authorise pages/actions per role. Effective roleAccess priority:
            employee override &gt; role permission &gt; default DENY.
          </p>
        </div>
      </div>

      {/* ============ CREATE / EDIT FORM ============ */}
      <div className="rm-card rm-form" ref={formRef}>
        <h3>{editingRole ? `Edit Role: ${editingRole.name}` : "Create Role"}</h3>
        <div className="rm-form-grid">
          <input
            placeholder="Role name (e.g. Operations Head)"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            disabled={Boolean(editingRole && Number(editingRole.is_system) === 1)}
            title={
              editingRole && Number(editingRole.is_system) === 1
                ? "System roles cannot be renamed"
                : ""
            }
          />
          <input
            placeholder="Description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
          />
        </div>
        <div className="rm-form-actions">
          <LoadingButton onClick={handleSaveRole} loading={savingRole} loadingText="Saving...">
            {editingRole ? "Update" : "Create Role"}
          </LoadingButton>
          {editingRole && (
            <button className="btn rm-outline" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
        {loadingError && <p className="rm-error">{loadingError}</p>}
      </div>

      {/* ============ ROLES TABLE ============ */}
      <div className="rm-card">
        <h3>Roles ({roles.length})</h3>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th scope="col">Role</th>
                <th scope="col">Description</th>
                <th scope="col">Type</th>
                <th scope="col">Users</th>
                <th scope="col">Perms</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rolesLoading && (
                <tr>
                  <td colSpan="7">Loading roles...</td>
                </tr>
              )}
              {roles.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.name}</strong>
                  </td>
                  <td>{r.description || "-"}</td>
                  <td>
                    <span className={Number(r.is_system) === 1 ? "rm-badge system" : "rm-badge custom"}>
                      {Number(r.is_system) === 1 ? "SYSTEM" : "CUSTOM"}
                    </span>
                  </td>
                  <td>{r.user_count}</td>
                  <td>{r.permission_count}</td>
                  <td>
                    <span className={`rm-status ${Number(r.is_active) === 1 ? "active" : "disabled"}`}>
                      {Number(r.is_active) === 1 ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td>
                    <div className="rm-row-actions">
                      <button
                        type="button"
                        className="rm-more-btn"
                        aria-label={`Actions for ${r.name}`}
                        aria-expanded={openRoleMenu === r.id}
                        onClick={(e) => toggleRoleMenu(e, r.id)}
                      >
                        &#8942;
                      </button>

                      {openRoleMenu === r.id && roleMenuPos &&
                        createPortal(
                          <div
                            className="rm-menu"
                            role="menu"
                            style={{
                              top: roleMenuPos.top,
                              bottom: roleMenuPos.bottom,
                              right: roleMenuPos.right,
                              maxHeight: roleMenuPos.maxH,
                            }}
                          >
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                closeRoleMenu();
                                openPermissions(r);
                              }}
                            >
                              Permissions
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                closeRoleMenu();
                                openUsers(r);
                              }}
                            >
                              Users
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                closeRoleMenu();
                                handleStartEdit(r);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                closeRoleMenu();
                                handleToggleActive(r);
                              }}
                            >
                              {Number(r.is_active) === 1 ? "Disable" : "Enable"}
                            </button>
                            <div className="rm-menu-sep" />
                            <button
                              type="button"
                              role="menuitem"
                              className="danger"
                              disabled={Number(r.is_system) === 1 || Number(r.user_count) > 0}
                              title={
                                Number(r.is_system) === 1
                                  ? "System roles cannot be deleted"
                                  : Number(r.user_count) > 0
                                    ? "Reassign users before deleting"
                                    : ""
                              }
                              onClick={() => {
                                closeRoleMenu();
                                handleDeleteRole(r);
                              }}
                            >
                              Delete
                            </button>
                          </div>,
                        document.body
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!rolesLoading && roles.length === 0 && (
                <tr>
                  <td colSpan="7">No roles found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="rm-hint">
          Hint: use "Permissions" to edit each role's page/action matrix, and
          "Users" to assign the role to an employee. Assignments can also be
          made from the Users tab role dropdown.
        </p>
      </div>

      {/* ============ PERMISSIONS MODAL ============ */}
      {permRole && (
        <div className="rm-overlay">
          <div className="rm-modal">
            <div className="rm-modal-header">
              <div className="rm-modal-title">
                <h3>Role Permissions</h3>
                <p>
                  <strong>{permRole.name}</strong>
                  <span className="rm-sep">|</span>
                  ☑ = permission enabled, ☐ = disabled. Unchecked = DENY.
                </p>
              </div>
              <button className="btn rm-close" onClick={() => setPermRole(null)}>×</button>
            </div>

            {roleAccessLoading ? (
              <p className="rm-empty">Loading permissions...</p>
            ) : (
              <div className="rm-access-body">
                {pageDivisions(pages).map((division) => (
                  <div className="rm-access-team" key={division}>
                    <h3 className="rm-access-team-title">{division}</h3>
                    {pages.filter((p) => p.division === division).map((p) => (
                      <div className="rm-access-page" key={p.page}>
                        <div className="rm-access-page-head">
                          <span className="rm-access-label">{p.label}</span>
                          {crossPageReqsFor(p.page).length > 0 && (
                            <span className="uac-page-deps">
                              Also needs:{" "}
                              {crossPageReqsFor(p.page).map(accessLabel).join(" + ")}
                            </span>
                          )}
                        </div>
                        <div className="rm-access-actions">
                          {p.actions.map((a) => {
                            const checked = getPermission(p.page, a.action) === "allow";
                            return (
                              <label
                                className={`rm-checkbox ${checked ? "on" : ""}`}
                                key={a.action}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    setPermission(p.page, a.action, checked ? "deny" : "allow")
                                  }
                                />
                                <span className="rm-action-name">{a.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div className="rm-modal-footer">
              <p className="rm-hint">
                Priority: employee-specific overrides still win over the role matrix.
              </p>
              <div className="rm-modal-actions">
                <button className="btn rm-outline" onClick={() => setPermRole(null)}>
                  Cancel
                </button>
                <LoadingButton onClick={handleSaveRoleAccess} loading={roleAccessSaving} loadingText="Saving...">
                  Save Permissions
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ USERS MODAL ============ */}
      {usersRole && (
        <div className="rm-overlay">
          <div className="rm-modal rm-modal-sm">
            <div className="rm-modal-header">
              <div className="rm-modal-title">
                <h3>Role Users</h3>
                <p>
                  <strong>{usersRole.name}</strong>
                  <span className="rm-sep">|</span>
                  Employees currently holding this role
                </p>
              </div>
              <button className="btn rm-close" onClick={() => setUsersRole(null)}>×</button>
            </div>

            <div className="rm-assign-section">
              <h4>Assign to an employee</h4>
              <div className="rm-assign-row">
                <select value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)}>
                  <option value="">Select employee...</option>
                  {availableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email || "no email"})
                    </option>
                  ))}
                </select>
                <LoadingButton onClick={handleAssign} loading={assigning} loadingText="Assigning...">
                  Assign
                </LoadingButton>
              </div>
              <p className="rm-hint">This sets the user's role; their roleAccess refreshes immediately.</p>
            </div>

            <div className="rm-assign-list">
              {roleUsersLoading ? (
                <p className="rm-empty">Loading users...</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Employee</th>
                      <th scope="col">Department</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roleUsers.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <strong>{u.name}</strong>
                          <div className="rm-email">{u.email}</div>
                        </td>
                        <td>{u.department || "-"}</td>
                        <td>
                          <span className={`rm-status ${Number(u.is_active) === 1 ? "active" : "disabled"}`}>
                            {Number(u.is_active) === 1 ? "Active" : "Disabled"}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {roleUsers.length === 0 && (
                      <tr>
                        <td colSpan="3">No users assigned to this role yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            <div className="rm-modal-footer">
              <div className="rm-modal-actions">
                <button className="btn rm-outline" onClick={() => setUsersRole(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      <ConfirmModal
        isOpen={!!roleToToggle}
        variant={roleToToggle && !(Number(roleToToggle.is_active) === 1) ? "warning" : "info"}
        title={roleToToggle ? `${!(Number(roleToToggle.is_active) === 1) ? "Enable" : "Disable"} role?` : ""}
        message={roleToToggle ? `${!(Number(roleToToggle.is_active) === 1) ? "Enable" : "Disable"} role "${roleToToggle.name}"?` : ""}
        confirmLabel={roleToToggle && !(Number(roleToToggle.is_active) === 1) ? "Enable" : "Disable"}
        cancelLabel="Cancel"
        isLoading={togglingRole}
        loadingText="Updating..."
        onConfirm={confirmToggleActive}
        onCancel={() => setRoleToToggle(null)}
      />
      <ConfirmModal
        isOpen={!!roleToDelete}
        variant="danger"
        title="Delete role?"
        message={roleToDelete ? `Delete role "${roleToDelete.name}"? This cannot be undone.` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={deletingRole}
        loadingText="Deleting..."
        onConfirm={confirmDeleteRole}
        onCancel={() => setRoleToDelete(null)}
      />
    </div>
  );
}

const styles = {
  page: {
    background: "transparent",
    fontFamily: "var(--font-sans)",
    color: "#0F172A",
  },
};

const css = `
.uac-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 20px; gap: 20px; flex-wrap: wrap;
}
.uac-header h2 { margin: 0; font-size: 24px; font-weight: 700; color: #0F172A; }
.uac-subtitle { color: #64748B; font-size: 13px; margin: 2px 0 0 0; }
.uac-outline { background: white; color: #1E40AF; border: 2px solid #CBD5E1; }
.uac-outline:disabled { opacity: 0.5; cursor: not-allowed; }
.uac-card { background: white; border-radius: 14px; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
.uac-empty { color: #94A3B8; padding: 24px; text-align: center; }
.uac-table-section h3 { margin: 0 0 12px 0; }
.table-wrapper { overflow-x: auto; }
table { width: 100%; border-collapse: separate; border-spacing: 0; }
th { padding: 10px; text-align: left; font-weight: 600; color: #334155; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #E2E8F0; background: #F8FAFC; white-space: nowrap; }
td { padding: 10px; border-bottom: 1px solid #F1F5F9; color: #0F172A; font-size: 13px; }
.uac-email { color: #64748B; font-size: 12px; }
.uac-status { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; }
.uac-status.active { background: #DCFCE7; color: #15803D; }
.uac-status.disabled { background: #FEE2E2; color: #B91C1C; }
.uac-row-actions { display: flex; gap: 6px; justify-content: flex-end; flex-wrap: wrap; }
.uac-manage { background: #EFF6FF; color: #1D4ED8; border: 1px solid #BFDBFE; }
.uac-manage:hover { background: #DBEAFE; }
.uac-manage-overlay {
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5);
  display: flex; align-items: center; justify-content: center; z-index: 1200; padding: 20px;
}
.uac-manage-modal {
  background: white; border-radius: 16px; width: 100%; max-width: 860px;
  max-height: 90vh; display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}
.uac-manage-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 18px 22px; border-bottom: 1px solid #E2E8F0;
}
.uac-manage-title h3 { margin: 0 0 4px 0; font-size: 18px; color: #0F172A; }
.uac-manage-title p { margin: 0; font-size: 13px; color: #64748B; }
.uac-sep { margin: 0 8px; color: #CBD5E1; }
.uac-close {
  background: #F1F5F9; color: #475569; border: none; border-radius: 8px;
  width: 32px; height: 32px; font-size: 16px; cursor: pointer; font-weight: 700;
}
.uac-close:hover { background: #E2E8F0; }
.uac-access-body { overflow-y: auto; padding: 16px 22px; display: grid; gap: 12px; }
.uac-access-team, .rm-access-team { display: grid; gap: 12px; }
.uac-access-team-title, .rm-access-team-title {
  margin: 6px 0 0; padding: 0 2px 6px; font-size: 12px; font-weight: 800;
  letter-spacing: 0.8px; text-transform: uppercase; color: #1D4ED8;
  border-bottom: 2px solid #E2E8F0;
}
.uac-access-page {
  border: 1px solid #E2E8F0; border-radius: 12px; padding: 12px 14px;
  background: #F8FAFC;
}
.uac-access-page-head { margin-bottom: 10px; }
.uac-access-label { font-weight: 700; font-size: 14px; color: #0F172A; }
.uac-access-actions { display: flex; flex-wrap: wrap; gap: 10px; }
.uac-access-action {
  display: flex; align-items: center; gap: 10px; background: white;
  border: 1px solid #E2E8F0; border-radius: 10px; padding: 8px 12px;
}
.uac-action-name { font-size: 12px; font-weight: 700; letter-spacing: 0.4px; color: #334155; }
.uac-toggle-group { display: inline-flex; gap: 4px; }
.uac-toggle {
  padding: 5px 12px; font-size: 11px; font-weight: 700; border-radius: 8px;
  border: 1px solid #CBD5E1; background: white; color: #64748B; cursor: pointer;
  font-family: var(--font-sans); transition: all 0.15s ease;
}
.uac-toggle:hover:not(.active) { border-color: #94A3B8; background: #F8FAFC; color: #334155; }
.uac-toggle:focus-visible {
  outline: 2px solid #2563EB; outline-offset: 1px;
}
.uac-toggle.allow.active { background: #16A34A; border-color: #16A34A; color: white; box-shadow: 0 0 0 1px #16A34A inset; }
.uac-toggle.allow.active:hover { background: #15803D; border-color: #15803D; }
.uac-toggle.deny.active { background: #DC2626; border-color: #DC2626; color: white; box-shadow: 0 0 0 1px #DC2626 inset; }
.uac-toggle.deny.active:hover { background: #B91C1C; border-color: #B91C1C; }
.uac-toggle.default.active {
  background: #475569; border-color: #475569; color: white;
  box-shadow: 0 0 0 1px #475569 inset;
}
.uac-toggle.default.active:hover { background: #334155; border-color: #334155; }
.uac-page-deps {
  font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
  color: #1D4ED8; background: #DBEAFE; border: 1px solid #BFDBFE;
  border-radius: 999px; padding: 2px 10px; margin-left: 10px;
}
.uac-effective {
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.4px; white-space: nowrap;
  border-radius: 999px; padding: 2px 9px;
}
.uac-effective.ok { background: #DCFCE7; color: #15803D; }
.uac-effective.no { background: #FEE2E2; color: #B91C1C; }
.uac-effective em { font-style: normal; font-weight: 700; color: inherit; opacity: 0.8; }
.uac-check {
  display: flex; align-items: center; gap: 8px; background: white;
  border: 1px solid #E2E8F0; border-radius: 10px; padding: 8px 12px;
  font-size: 12px; font-weight: 700; color: #334155; cursor: pointer;
}
.uac-check input { width: 15px; height: 15px; accent-color: #16A34A; cursor: pointer; }
.uac-check.checked { border-color: #16A34A; background: #F0FDF4; color: #15803D; }
.uac-manage-footer {
  padding: 14px 22px; border-top: 1px solid #E2E8F0;
  display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;
}
.uac-hint { margin: 0; font-size: 12px; color: #64748B; }
.uac-manage-actions { display: flex; gap: 10px; }
.rm-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; gap: 20px; flex-wrap: wrap; }
.rm-header h2 { margin: 0; font-size: 24px; font-weight: 700; color: #0F172A; }
.rm-subtitle { color: #64748B; font-size: 13px; margin: 2px 0 0 0; }
.rm-card { background: white; border-radius: 14px; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); margin-bottom: 18px; }
.rm-card h3 { margin: 0 0 12px 0; font-size: 16px; }
.rm-form-grid { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.rm-form-grid input { flex: 1; min-width: 220px; padding: 10px 12px; border: 1px solid #CBD5E1; border-radius: 10px; font-size: 13px; font-family: var(--font-sans); }
.rm-form-actions { display: flex; gap: 10px; }
.rm-error { color: #B91C1C; font-size: 13px; margin-top: 8px; }
.btn { padding: 8px 14px; border: none; border-radius: 10px; cursor: pointer; font-weight: 600; font-size: 12px; font-family: var(--font-sans); display: inline-flex; align-items: center; gap: 6px; transition: all 0.2s ease; white-space: nowrap; }
.rm-outline { background: white; color: #1E40AF; border: 2px solid #CBD5E1; }
.rm-outline:disabled { opacity: 0.5; cursor: not-allowed; }
.rm-manage { background: #EFF6FF; color: #1D4ED8; border: 1px solid #BFDBFE; }
.rm-users { background: #F0FDF4; color: #15803D; border: 1px solid #BBF7D0; }
.rm-danger { background: #FEF2F2; color: #B91C1C; border: 1px solid #FECACA; }
.rm-danger:disabled { opacity: 0.4; cursor: not-allowed; }
.rm-badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; letter-spacing: 0.4px; }
.rm-badge.system { background: #DBEAFE; color: #1D4ED8; }
.rm-badge.custom { background: #F1F5F9; color: #475569; }
.rm-email { color: #64748B; font-size: 12px; }
.rm-status { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; }
.rm-status.active { background: #DCFCE7; color: #15803D; }
.rm-status.disabled { background: #FEE2E2; color: #B91C1C; }
.rm-row-actions { display: flex; flex-wrap: wrap; gap: 6px 8px; justify-content: flex-end; align-items: center; }

.rm-hint { margin: 12px 0 0 0; font-size: 12px; color: #64748B; }
.rm-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5); display: flex; align-items: center; justify-content: center; z-index: 1200; padding: 20px; }
.rm-modal { background: white; border-radius: 16px; width: 100%; max-width: 860px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3); }
.rm-modal-sm { max-width: 640px; }
.rm-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 18px 22px; border-bottom: 1px solid #E2E8F0; }
.rm-modal-title h3 { margin: 0 0 4px 0; font-size: 18px; color: #0F172A; }
.rm-modal-title p { margin: 0; font-size: 13px; color: #64748B; }
.rm-sep { margin: 0 8px; color: #CBD5E1; }
.rm-close { background: #F1F5F9; color: #475569; border: none; border-radius: 8px; width: 32px; height: 32px; font-size: 16px; cursor: pointer; font-weight: 700; }
.rm-close:hover { background: #E2E8F0; }
.rm-access-body { overflow-y: auto; padding: 16px 22px; display: grid; gap: 12px; }
.rm-access-page { border: 1px solid #E2E8F0; border-radius: 12px; padding: 12px 14px; background: #F8FAFC; }
.rm-access-page-head { margin-bottom: 10px; }
.rm-access-label { font-weight: 700; font-size: 14px; color: #0F172A; }
.rm-access-actions { display: flex; flex-wrap: wrap; gap: 10px; }
.rm-checkbox { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; background: white; border: 1px solid #E2E8F0; border-radius: 10px; padding: 8px 12px; transition: all 0.15s ease; }
.rm-checkbox input { width: 17px; height: 17px; accent-color: #16A34A; cursor: pointer; margin: 0; }
.rm-checkbox.on { border-color: #86EFAC; background: #F0FDF4; }
.rm-checkbox:not(.on) { border-color: #E2E8F0; }
.rm-action-name { font-size: 12px; font-weight: 700; letter-spacing: 0.4px; color: #334155; }
.rm-modal-footer { padding: 14px 22px; border-top: 1px solid #E2E8F0; display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
.rm-modal-actions { display: flex; gap: 10px; }
.rm-empty { color: #94A3B8; padding: 24px; text-align: center; }
.rm-assign-section { padding: 18px 22px 0 22px; }
.rm-assign-section h4 { margin: 0 0 10px 0; font-size: 14px; color: #0F172A; }
.rm-assign-row { display: flex; gap: 10px; align-items: center; }
.rm-assign-row select { flex: 1; padding: 10px 12px; border: 1px solid #CBD5E1; border-radius: 10px; font-size: 13px; font-family: var(--font-sans); }
.rm-assign-list { padding: 16px 22px 4px 22px; overflow-y: auto; }

/* ===== UI polish: spacing, hierarchy, buttons (behaviour unchanged) ===== */
.rm-card, .uac-card { padding: 18px 20px; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 4px 14px rgba(15, 23, 42, 0.05); }
.rm-card h3, .uac-table-section h3 { font-size: 15px; letter-spacing: 0.2px; }
.btn { padding: 7px 13px; font-size: 12px; border-radius: 8px; }
.rm-access-body, .uac-access-body { gap: 14px; }
.rm-checkbox, .uac-check, .uac-access-action { padding: 7px 11px; }
.uac-toggle { min-width: 64px; text-align: center; }
.rm-card tbody tr:hover td, .uac-card tbody tr:hover td { background: #F8FAFC; }
.rm-hint, .uac-hint { font-size: 11.5px; line-height: 1.5; }

/* ===== ROLES PAGE POLISH (light enterprise SaaS) =====
   Presentation only: no handler, API, permission or data behaviour changes.
   Refines the Role Management view to match the light HR Portal dashboard. */

/* Section spacing so the page breathes instead of boxing every element. */
.rm-header { margin-bottom: 22px; }
.rm-header h2 { font-size: 28px; color: #172B4D; }
.rm-subtitle { color: #64748B; max-width: 760px; line-height: 1.55; }

/* Cards: white, hairline border, very soft shadow. */
.rm-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  padding: 20px 22px;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05);
  margin-bottom: 20px;
}
.rm-card h3 { font-size: 20px; font-weight: 700; color: #172B4D; margin-bottom: 16px; }
.rm-form { background: #FFFFFF; }

/* Create/Edit role inputs. */
.rm-form-grid input {
  background: #FFFFFF !important;
  color: #172B4D !important;
  border: 1px solid #CBD5E1 !important;
  border-radius: 10px;
  padding: 11px 14px;
}
.rm-form-grid input::placeholder { color: #94A3B8; }
.rm-form-grid input:focus {
  outline: none;
  border-color: #2563EB !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}
.rm-form-grid input:disabled { background: #F8FAFC !important; color: #94A3B8 !important; }

/* Table: light header, white rows, subtle separators. */
/* Roles table: cap the height to the viewport and scroll inside the card, so
   every role stays reachable. The global ".table-wrapper" rule no longer caps
   height app-wide, so this rule sets the cap itself; the old "overflow: hidden"
   here suppressed the scrollbar, permanently clipping the last rows. */
.rm-card .table-wrapper {
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  overflow: auto;
  overscroll-behavior: contain;
  /* Floor of 320px keeps the table usable in short windows. */
  max-height: max(320px, calc(100vh - 230px));
}
/* Keep column headers visible while rows scroll under them. */
.rm-card table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}
.rm-card table th {
  background: #F8FAFC;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  border-bottom: 1px solid #E2E8F0;
}
.rm-card table td {
  background: #FFFFFF;
  color: #334155;
  border-bottom: 1px solid #E2E8F0;
}
.rm-card table tbody tr:last-child td { border-bottom: none; }
.rm-card table tbody tr:hover td { background: #F8FAFC; }
.rm-card table td strong { color: #172B4D; font-weight: 600; }

/* Soft pastel badges (no dark fills). */
.rm-badge { padding: 3px 10px; border-radius: 999px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.4px; }
.rm-badge.system { background: #EFF6FF; color: #2563EB; }
.rm-badge.custom { background: #F5F3FF; color: #7C3AED; }
.rm-status { padding: 3px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; }
.rm-status.active { background: #ECFDF5; color: #059669; }
.rm-status.disabled { background: #FEF2F2; color: #DC2626; }

/* Compact three-dot row action menu. */
.rm-row-actions { position: relative; justify-content: flex-end; }
.rm-more-btn {
  width: 32px; height: 32px;
  display: inline-flex; align-items: center; justify-content: center;
  background: #FFFFFF; color: #64748B;
  border: 1px solid #E2E8F0; border-radius: 8px;
  cursor: pointer; font-size: 16px; line-height: 1;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.rm-more-btn:hover { background: #F8FAFC; border-color: #CBD5E1; color: #172B4D; }
.rm-more-btn:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }

/* Portalled to document.body and anchored to the trigger button with fixed
   coordinates (see toggleRoleMenu), so it escapes the table wrapper's
   "overflow: hidden" clip and never gets painted over by neighbour rows. */
.rm-menu {
  position: fixed;
  min-width: 176px; padding: 6px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
  /* Above the fixed bottom-right widgets that share this corner — chatbot FAB
     (--z-chatbot), scroll-to-top and toasts — so they can never intercept
     clicks meant for a menu item; still below every overlay/modal. */
  z-index: calc(var(--z-chatbot) + 10);
  /* "max-height" comes from the inline style set in toggleRoleMenu (the room
     left next to the trigger), so a menu taller than that space scrolls
     instead of running off the bottom of the viewport. */
  overflow-y: auto;
  overscroll-behavior: contain;
}
.rm-menu button {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 8px 10px;
  background: none; border: none; border-radius: 8px;
  color: #172B4D; font-size: 13px; font-weight: 500;
  font-family: var(--font-sans); text-align: left; cursor: pointer;
  white-space: nowrap;
}
.rm-menu button:hover:not(:disabled) { background: #F8FAFC; }
.rm-menu button:disabled { color: #CBD5E1; cursor: not-allowed; }
.rm-menu button.danger { color: #DC2626; }
.rm-menu button.danger:hover:not(:disabled) { background: #FEF2F2; }
.rm-menu-sep { height: 1px; margin: 5px 4px; background: #E2E8F0; }

/* Slim scrollbar for the menu when it has to scroll (portalled to body, so
   the .rm-card scrollbar rules above do not reach it). */
.rm-menu::-webkit-scrollbar { width: 8px; }
.rm-menu::-webkit-scrollbar-track { background: transparent; }
.rm-menu::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 8px; }
.rm-menu::-webkit-scrollbar-thumb:hover { background: #94A3B8; }

/* Hint text and errors. */
.rm-hint { color: #64748B; margin-top: 12px; }
.rm-error { color: #DC2626; }

/* Light scrollbars inside the table (scrolling behaviour unchanged). */
.rm-card .table-wrapper::-webkit-scrollbar { height: 9px; width: 9px; }
.rm-card .table-wrapper::-webkit-scrollbar-track { background: #F1F5F9; }
.rm-card .table-wrapper::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 999px; }
.rm-card .table-wrapper::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
.rm-card .table-wrapper { scrollbar-width: thin; scrollbar-color: #CBD5E1 #F1F5F9; }

`;

